import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../../components/tailwind-component/ui/card';
import { Button } from '../../../../../components/tailwind-component/ui/button';
import { Badge } from '../../../../../components/tailwind-component/ui/badge';
import { Switch } from '../../../../../components/tailwind-component/ui/switch';
import { Plus, ExternalLink, Trash2, Image as ImageIcon, Zap, PlayCircle } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
// import { toast } from 'sonner';
import { Methods } from '../../../../../core/utility';
import { PostWithAutomationMeta } from 'custom-type';
import zapIconWhite from '../../../../../assets/images/zapIconWhite.svg';

const methods: Methods = new Methods()

interface PostManagerProps {
  posts: PostWithAutomationMeta[];
  handleTogglePost: (ig_automation_id: number, ig_automation_post_id: number, status: boolean) => void;
  handleDeletePost: (
    post: PostWithAutomationMeta
  ) => void;
  // new props for pagination loading
  isLoading: boolean;
  hasMore: boolean;
  loadMoreRef: any;
  total_post: number;
  fetchPosts: (page: number) => Promise<void>;
  page: number;
  // Opens the "How to use Zap" walkthrough (ZapTourModal). The modal's open state
  // lives in ZapAutomations, so the handler is threaded down through AccountSettings.
  // Optional: the CTA is simply not rendered when no handler is supplied.
  onHowToUseZap?: () => void;
}

export function PostManager({ 
  posts, handleTogglePost, 
  handleDeletePost, 
  isLoading, 
  hasMore, 
  loadMoreRef, 
  total_post,
  fetchPosts,
  page,
  onHowToUseZap
}: PostManagerProps) {
  // const activePosts = posts.filter((p) => p?.is_active).length;

  const PostSkeleton = () => {
    return (
      <Card className="border animate-pulse">
        <CardContent className="p-3 md:p-4">
          <div className="flex items-start gap-3 md:gap-4">
            
            {/* Thumbnail */}
            <div className="w-16 h-16 bg-gray-200 rounded-lg" />

            {/* Content */}
            <div className="flex-1 space-y-2">
              
              {/* Automation name + status */}
              <div className="flex justify-between items-start">
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="h-5 w-16 bg-gray-200 rounded-full" />
              </div>

              {/* Caption */}
              <div className="space-y-1">
                <div className="h-3 w-full bg-gray-200 rounded" />
                <div className="h-3 w-3/4 bg-gray-200 rounded" />
              </div>

              {/* Trigger count */}
              <div className="h-3 w-28 bg-gray-200 rounded" />

              {/* Keywords */}
              <div className="space-y-1">
                <div className="h-3 w-20 bg-gray-200 rounded" />
                <div className="flex gap-2 flex-wrap">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="h-5 w-14 bg-gray-200 rounded-full"
                    />
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-2">
                <div className="h-3 w-32 bg-gray-200 rounded" />
                <div className="flex items-center gap-3">
                  <div className="h-5 w-10 bg-gray-200 rounded-full" />
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const getMediaLabel = (mediaType: string | null) => {
    switch (mediaType) {
      case 'STORY':
        return 'Story';
      case 'FEED': 
      case 'REELS':
        return 'Post';
      default:
        return mediaType;
    }
  };

  return (
    <div className="space-y-6 max-md:space-y-3">
      <Card className='p-0 border-0  max-md:shadow-none  max-md:bg-transparent' >
        <CardHeader className='p-0'>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Posts</CardTitle>
              <CardDescription>
                Monitor Instagram posts for comments and automate responses
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className='px-0 pt-2'>
          <div className="mb-4 p-4 bg-[#e4f5fb] border border-[#cdf0fc] rounded-lg 
          max-md:bg-[#EAF7FB] max-md:border-[#D6EEF7] max-md:mb-3 max-md:p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Active Posts</p>
                <p className="text-gray-600 text-xs">
                  Posts being monitored for comments
                </p>
              </div>
              <div className="text-right">
                {/* <p className="text-2xl">{activePosts}</p>
                <p className="text-xs text-gray-500">of {posts.length}</p> */}
                <p className="text-2xl">{posts.length}</p>
                <p className="text-xs text-gray-500">of {total_post}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {posts.length === 0 && isLoading && (
              [...Array(4)].map((_, i) => <PostSkeleton key={i} />)
            )}
            {
              posts?.length === 0 
              ? 
              (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No posts added yet</p>
                  <p className="text-sm">Add your first Instagram post to start automating</p>
                </div>
              ) 
              : 
              (
                posts.map((post) => (
                  <div
                    key={post?.ig_automation_post_id}
                    className="flex items-start gap-2 md:gap-4 p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors max-md:border-[#E5E7EB] bg-white max-md:rounded-2xl"
                  >
                    <ImageWithFallback
                      src={post?.gcp_media_file_url || ''}
                      alt="Post thumbnail"
                      className="w-16 h-16 object-cover max-md:w-[58px] max-md:h-[58px] max-md:rounded-xl"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 ">
                          {
                            Boolean(post?.automation_name) && 
                            <p className="text-md text-gray-600 items-start flex gap-1 text-[14px] md:text-[16px] max-md:flex-col leading-[16px] pb-1 max-md:text-[#2B2B2B]">
                              {post.automation_name}
                              {post.has_zap_link && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full self-start" style={{ 
                                  background: 'linear-gradient(135deg, #9C52A2 0%, #e1306c 50%, #FA8E41 100%)', 
                                  color: '#fff',  }}>
                                  {/* <Zap className="w-3 h-3 fill-white" /> */}
                                  <img src={zapIconWhite} alt="Zap Logo" width={7} height={7} />
                                  Zap Link
                                </span>
                              )}
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-[#2b2b2b] max-md:hidden"
                                onClick={() => window.open(post?.permalink, '_blank')}
                              >
                                <ExternalLink className="w-3 h-3 ml-1" />
                              </Button>
                            </p>
                          }
                          {post?.caption  &&
                            <p className={`text-sm  line-clamp-2 mb-1`}>{post?.caption}</p>
                          }
                          {
                            Boolean(post?.trigger_count) && 
                            <p className="text-xs text-gray-600">
                              <span className="font-medium">Trigger Count:</span> {post.trigger_count}
                            </p>
                          }
                        </div>
                        <Badge variant={post?.is_active ? 'outline' : 'outline'} 
                        // className={`ml-2 z-0 ${post?.is_active ? "btn-primary" : "btn-default" } btn `}
                        className={`ml-2 max-md:text-[11px] max-md:px-2 max-md:py-[2px] max-md:rounded-md max-md:border-0 ${
                          post?.is_active
                            ? "bg-black text-white"
                            : "bg-[#E5E7EB] text-[#6B7280]"
                        }`}
                        >
                          {post?.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      
                      {/* Automation Metadata */}
                      <div className="mb-3">
                        {/* Keywords Section */}
                        {
                          Boolean(post?.keywords) &&
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Keywords</p>
                            <div className="flex gap-2 flex-wrap">
                              {post.keywords?.map((kw, idx) => (
                                <Badge key={kw + idx} variant="default" className="text-xs z-0 *:
                              bg-[#00aae5] max-md:hover:bg-[#38BDF8] max-md:text-white max-md:text-[10px] max-md:px-2 max-md:py-[2px] max-md:rounded-md max-md:border-0
                                ">
                                  {kw}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        }
                      </div>
                      <div className='flex items-center max-md:pt-2 max-md:border-t max-md:mt-2'>
                        <p className="text-xs text-gray-500 hidden md:block">
                          {/* Added on {new Date(post?.created_at || Date.now()).toLocaleDateString()} */}
                          {/* Created {methods.formatDate(post?.created_at)} */}
                          {/* Added on{" "}
                          {methods.formatDateForDisplay(new Date(post?.created_at || Date.now()))} */}
                          {getMediaLabel(post.media_product_type)} Added on{" "}
                          {methods.formatDateForDisplay(new Date(post?.created_at || Date.now()))}
                        </p>
                        <div className="flex items-center gap-3 md:ms-auto md:border-0 max-md:w-full">
                          <Switch
                            checked={post?.is_active}
                            onCheckedChange={() => handleTogglePost(post?.ig_automation_id || 0, post?.ig_automation_post_id || 0, !post.is_active)}
                          />
                          <Button variant="ghost"
                            size="sm"
                            className='p-2'
                            onClick={() => handleDeletePost(post)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 ms-auto text-xs text-[#2b2b2b] md:hidden"
                                onClick={() => window.open(post?.permalink, '_blank')}
                              >
                                <ExternalLink className="w-3 h-3 ml-1" />
                                View on Instagram
                              </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )
            }
            {hasMore && isLoading && posts.length > 0 && (
              [...Array(3)].map((_, i) => <PostSkeleton key={`more-${i}`} />)
            )}
            {hasMore && (
              <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  // onClick={() => setPostPage(p => p + 1)}
                  onClick={() => fetchPosts(page)}
                  disabled={isLoading === true}
                >
                  {isLoading ? "Loading..." : "Load more posts"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">1</span>
              <span>Add Instagram posts you want to monitor for comments</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">2</span>
              <span>Create automation rules with keyword triggers for these posts</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">3</span>
              <span>When someone comments with a keyword, they'll get an automated comment reply and DM</span>
            </li>
          </ol>

          {/* Same "How to use Zap" walkthrough that the Create-New-Zap tab offers.
              Surfaced here too so the full guide is reachable from the My Zaps tab,
              where users actually land once they have automations. */}
          {onHowToUseZap && (
            <div className="flex justify-center mt-5">
              <button
                type="button"
                onClick={onHowToUseZap}
                className="inline-flex items-center gap-2 text-[14px] font-medium text-[#9C52A2] border border-[#e8d3ec] bg-[#faf5fb] hover:bg-[#f3e6f5] rounded-full px-4 py-2 transition"
              >
                <PlayCircle className="w-4 h-4" /> How to use Zap
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* <AddPostDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleAddPost}
      /> */}
    </div>
  );
}